import { useCheckboxSelectField } from '@atlassian/jira-issue-field-checkbox-select/src/services/use-checkbox-select-field/index.tsx';
import type { CheckboxSelectValue } from '@atlassian/jira-issue-field-checkbox-select/src/services/use-checkbox-select-field/types.tsx';
import { MULTI_CHECKBOXES_CF_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { createClipboardHelperComponent } from '../../../clipboard-handler/create-clipboard-helper-component/index.tsx';
import { useClipboardHandler } from '../../../clipboard-handler/use-clipboard-handler/index.tsx';
import {
	assertClipboardDataIsCompatible,
	assertClipboardHasRichFormat,
} from '../../../clipboard-handler/utils.tsx';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	ListIssueForCopyPaste,
	PasteHandler,
	PasteMetadata,
} from '../../../types.tsx';
import { checkboxSupportedTypes } from '../supported-field-types/index.tsx';
import type { TransformedCheckboxOption, TransformedCheckboxField } from '../types.tsx';

const isTransformedCheckboxOption = (option: unknown): option is TransformedCheckboxOption =>
	!!option && typeof option === 'object' && 'optionId' in option && 'value' in option;

const isTransformedCheckbox = (fieldValue: unknown): fieldValue is TransformedCheckboxOption[] =>
	Array.isArray(fieldValue) && fieldValue.every(isTransformedCheckboxOption);

const isCheckboxValue = (fieldValue: unknown): fieldValue is CheckboxSelectValue =>
	Array.isArray(fieldValue) &&
	fieldValue.every(
		(option) => !!option && typeof option === 'object' && 'id' in option && 'value' in option,
	);

const emptyValue: [] = [];

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): CheckboxSelectValue => {
	if (
		!assertClipboardHasRichFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, checkboxSupportedTypes)
	) {
		return [];
	}

	if (!isCheckboxValue(metadata?.fieldValue)) {
		return [];
	}

	return metadata?.fieldValue ?? [];
};

function assertCheckboxField(
	field: ListIssueFieldTransformed,
): asserts field is TransformedCheckboxField {
	if (!field) {
		throw new Error('Field is required');
	}

	if (field.type !== MULTI_CHECKBOXES_CF_TYPE) {
		throw new Error('Field type is not supported');
	}
}
const useCheckboxClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	assertCheckboxField(field);

	const [{ value }, { saveValue }] = useCheckboxSelectField({
		fieldKey: field.fieldId,
		issueKey: issue.key,
		initialValue: isTransformedCheckbox(field.value)
			? field.value
					.filter(Boolean)
					.map((option) => ({ id: option.optionId, value: option.value ?? '' }))
			: undefined,
	});

	return useClipboardHandler({
		issue,
		field,
		currentStringValue: value?.map((option) => option?.value || '').join(', ') || '',
		currentValue: value,
		getPastedValue,
		saveValue,
		emptyValue,
	});
};

const CheckboxClipboardHelper = createClipboardHelperComponent(useCheckboxClipboardHandler);

export const checkboxClipboardHandler = {
	[MULTI_CHECKBOXES_CF_TYPE]: CheckboxClipboardHelper,
};
